import CloseIcon from "@mui/icons-material/CloseRounded";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import { useTranslation } from "@/translations";

interface Props {
  folderPath: string;
  onChangeFolder: () => void;
  onReset: () => void;
  onClose: () => void;
}

/**
 * What a working grant offers: the folder it points at, and the two things that can be done to it.
 * They live here rather than on the card so a grant that is simply fine stays quiet.
 *
 * Deliberately absent is whether the browser granted the folder for this session only or for good —
 * queryPermission answers "granted" either way, so the application cannot know.
 */
export function MailMergeManageDialog({ folderPath, onChangeFolder, onReset, onClose }: Props) {
  const { t } = useTranslation();

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ p: 5, pb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}
        variant="h1"
      >
        {t("mailMerge.access.manageDialog.title")}
        <IconButton onClick={onClose} aria-label={t("common.close")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 5, pt: 0 }}>
        <Stack spacing={1}>
          <StatusRow label={t("mailMerge.access.manageDialog.folderLabel")} value={folderPath} monospace />
          <StatusRow
            label={t("mailMerge.access.manageDialog.statusLabel")}
            value={t("mailMerge.access.manageDialog.statusGranted")}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 5, pt: 0, gap: 2, justifyContent: "space-between" }}>
        <Button variant="outlined" onClick={onClose}>
          {t("common.close")}
        </Button>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={onChangeFolder}>
            {t("mailMerge.access.actions.change")}
          </Button>
          <Button variant="outlined" color="error" onClick={onReset}>
            {t("mailMerge.access.actions.reset")}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

function StatusRow({ label, value, monospace = false }: { label: string; value: string; monospace?: boolean }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 96 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="bold" sx={monospace ? { fontFamily: "monospace" } : undefined}>
        {value}
      </Typography>
    </Stack>
  );
}
